import { ConnectionIcon, CopyIcon, DragIcon, TrashIcon } from '@mm/icons';
import { ReactComponent as AlertAction } from 'assets/icons/alert-action.svg';
import { ReactComponent as ConditionalLogicOnIcon } from 'assets/icons/conditional-logic-on-sbs.svg';
import React, { useCallback, useContext, useState } from 'react';
import { DraggableProvided } from 'react-beautiful-dnd';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { useWidgetCRUDActions } from '@mm/state/actions/WidgetCRUDActions';
import { PlatformType } from '@mm/state/model/CampaignsModel';
import WidgetModel from '@mm/state/model/WidgetModel';
import modalActions from '@mm/state/modules/modalsState/actions';
import { StoreState } from '@mm/state/store-interfaces';
import { colors } from '../../../../editor/features/beta/styles/colors';
import ConditionalLogic from '../../../../editor/features/build/components/widgets/makeWidget/ConditionalLogic';
import { CopyLogic } from '../../../../editor/features/build/components/widgets/makeWidget/CopyLogic';
import { getWidgetErrors } from '../../../../editor/features/build/components/widgets/makeWidget/get-widget-errors';
import { WidgetContainerRefProp } from '../../../../editor/features/startBotPromoters/common/StartBotsDialogueEditor/WidgetByType/WidgetByType';
import { useIsLeadGeneratorBuilderRoute } from '../../../../modules/FreeLeadGenerator/use-is-lead-generator-builder-route';
import { PrimaryToolTip } from '../../../components/PrimaryToolTip/PrimaryToolTip';
import { useCommonWidgetHandlers } from '../../../helpers/widget-helpers/use-common-widget-handlers';
import { getNodeName, PathGraphContext } from '../PathGraph/PathGraph';
import { successToast } from '../../../helpers/toast';
import './WidgetControls.pcss';
import { WidgetLayout } from '../WidgetLayout/WidgetLayout';

type Props = {
  widget: WidgetModel;
  platforms?: PlatformType[];
  beforeDestroy?: () => any;
  afterDestroy?: () => any;
  draggableProvided?: DraggableProvided;
  disableDragging?: boolean;
  disableDeleting?: boolean;
  disableCopying?: boolean;
  disableConditionalLogic?: boolean;
  deleteModalHeaderText?: string;
} & WidgetContainerRefProp;

const Controls = (props: Props) => {
  const {
    widget,
    beforeDestroy,
    afterDestroy,
    draggableProvided,
    disableDragging,
    disableDeleting,
    disableCopying,
    disableConditionalLogic,
    deleteModalHeaderText,
    platforms = ['facebook'],
  } = props;
  const dispatch: Dispatch<StoreState> = useDispatch();

  const platformsInWidget = Object.keys(widget.validatedByPlatforms || {});
  const isPlatformPresent = platforms?.find(p =>
    platformsInWidget.find(platform => p === platform),
  );
  const isValidToPlatform = platforms?.every(
    p => widget.validatedByPlatforms && widget.validatedByPlatforms[p],
  );
  const isValid = isPlatformPresent
    ? isValidToPlatform && !widget.validated.buttonDialogueInValid
    : !widget.validated.buttonDialogueInValid;

  const commonWidgetHandlers = useCommonWidgetHandlers();
  const errors = getWidgetErrors(widget, isValid);

  const widgetCRUDActions = useWidgetCRUDActions();
  const [showCopying, setShowCopying] = useState(false);

  const {
    addDialogueWidgetsToGraph,
    turnSuccessorsActiveNodesOff,
    removeNodeAndSuccessors,
  } = useContext(PathGraphContext);

  const onCopyIconClick = () => {
    setShowCopying(true);
  };

  const onCopy = async (dialogueId: number) => {
    const clonedWidget = await widgetCRUDActions.clone(widget.id, dialogueId);
    turnSuccessorsActiveNodesOff?.(getNodeName(dialogueId, 'dialogue'));
    if (clonedWidget) {
      addDialogueWidgetsToGraph?.([clonedWidget]);
      successToast(`Widget successfully cloned to your selected dialogue`);
    }
  };

  const onClickDelete = () => {
    const deleteCallback = async () => {
      if (beforeDestroy) {
        beforeDestroy();
      }

      removeNodeAndSuccessors?.({
        id: widget.id,
        type: 'widget',
        actionType: 'delete',
        parentId: widget.pageId,
        parentType: 'dialogue',
      });

      await commonWidgetHandlers.delete(widget);

      if (afterDestroy) {
        afterDestroy();
      }
    };

    dispatch(
      modalActions.openDeleteConfirmModal({
        deleteCallback,
        headerText:
          deleteModalHeaderText ||
          'Are you sure you want to delete this widget?',
      }),
    );
  };

  const [showConditionalLogic, setShowConditionalLogic] = useState(false);

  const toggleConditionalLogic = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setShowConditionalLogic(showConditionalLogic => !showConditionalLogic);
  };

  const onUpdateConditionalLogic = useCallback(
    async (partialWidget: Partial<WidgetModel>) => {
      const updatedWidget = { ...widget, ...partialWidget };
      await widgetCRUDActions.update(updatedWidget);
    },
    [widget, widgetCRUDActions],
  );

  const isSlideoutEditor = useSelector<StoreState, boolean>(state =>
    state.view.slideoutMenu.some(
      menu =>
        menu.menuType &&
        [
          'editor',
          'qaEditor',
          'smsKeywordEditor',
          'igWelcomerEditor',
          'igStoryMentionEditor',
          'igCommentGuardEditor',
          'igStoryReactionEditor',
          'fbWelcomerEditor',
          'smsBlasterEditor',
        ].includes(menu.menuType),
    ),
  );

  const isLeadGeneratorRoute = useIsLeadGeneratorBuilderRoute();

  const dragHandleProps = draggableProvided
    ? draggableProvided.dragHandleProps
    : {};

  return (
    <div className="widget-controls">
      <div className="widget-controls__wrapper">
        {!isLeadGeneratorRoute && (
          <>
            {widget.conditionalLogicExists ? (
              <ConditionalLogicOnIcon
                onClick={toggleConditionalLogic}
                className="widget-controls__conditional-logic--icon"
              />
            ) : (
              !disableConditionalLogic && (
                <ConnectionIcon
                  color={colors.blue}
                  onClick={toggleConditionalLogic}
                  className="widget-controls__conditional-logic--icon"
                />
              )
            )}
          </>
        )}
        {showConditionalLogic && (
          <ConditionalLogic
            widget={widget}
            onUpdateConditionalLogic={onUpdateConditionalLogic}
            onClose={() => setShowConditionalLogic(false)}
            isStartBotsLayout
          />
        )}
        {!disableDragging && (
          <DragIcon
            color={colors.blue}
            className="widget-controls__drag-handle"
            {...dragHandleProps}
          />
        )}
        {!disableCopying && (
          <>
            <CopyIcon
              color={colors.blue}
              onClick={onCopyIconClick}
              className="widget-controls__trash-icon"
            />
            {showCopying && (
              <CopyLogic
                onCopyWidget={onCopy}
                onClose={() => setShowCopying(false)}
                initialDialogueId={widget.pageId}
              />
            )}
          </>
        )}
        {!disableDeleting && (
          <TrashIcon
            color={colors.blue}
            onClick={onClickDelete}
            className="widget-controls__trash-icon"
          />
        )}
        {!isValid && (
          <PrimaryToolTip
            title="Widget Errors"
            align="center"
            position={isSlideoutEditor ? 'left' : 'bottom'}
            anchorElement={
              <AlertAction className="widget-controls__alert-icon" />
            }
            useHover={true}
            classNameContainer="widget-controls__tool-tip-area"
          >
            {errors.widget}
          </PrimaryToolTip>
        )}
      </div>
    </div>
  );
};

export const WidgetControls = (props: Props) => {
  const { draggableProvided, ...rest } = props;
  return (
    <span {...(draggableProvided?.dragHandleProps || {})}>
      <WidgetLayout.ControlsPortal widget={props.widget}>
        <Controls {...rest} />
      </WidgetLayout.ControlsPortal>
    </span>
  );
};
